import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { FileText, ArrowLeft, Loader2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 }).format(n || 0);
const STATUS_BADGE = {
  draft: "bg-amber-100 text-amber-700",
  issued: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-slate-100 text-slate-500",
};

export default function CreditNotesPage() {
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get("highlight") || "";
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/builder/credit-notes/list`, { withCredentials: true });
        setNotes(res.data.credit_notes || []);
      } catch { setNotes([]); }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-4 sm:py-8" data-testid="credit-notes-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/invoices" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Invoices
        </Link>
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Credit Notes
          </h1>
          <p className="text-sm text-slate-500 mt-1">Reverse or adjust issued invoices while preserving accounting integrity.</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : notes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-10 text-center" data-testid="credit-notes-empty">
            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700 mb-1">No Credit Notes yet</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Open a locked Sales invoice (paid or shared &gt; 24 hours ago) and click <span className="font-semibold">Issue Credit Note</span> to create one here.
            </p>
            <Link to="/invoices?type=sales" className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold">
              View Sales Invoices
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto" data-testid="credit-notes-list">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Number</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Original Invoice</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase tracking-wider">Issue Date</th>
                </tr>
              </thead>
              <tbody>
                {notes.map(n => (
                  <tr
                    key={n.credit_note_id}
                    className={`border-b border-slate-50 last:border-0 hover:bg-slate-50 cursor-pointer ${highlight === n.credit_note_id ? "bg-amber-50/60" : ""}`}
                    onClick={() => navigate(`/credit-notes/${n.credit_note_id}`)}
                    data-testid={`credit-note-row-${n.credit_note_id}`}
                  >
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-900">{n.credit_note_number || n.credit_note_id}</td>
                    <td className="px-4 py-3 text-slate-900">{n.customer_name || "—"}</td>
                    <td className="px-4 py-3">
                      {n.original_invoice_id ? (
                        <Link to={`/invoices/${n.original_invoice_id}`} onClick={(e) => e.stopPropagation()} className="text-xs font-mono text-blue-600 hover:underline inline-flex items-center gap-1">
                          {n.original_invoice_number || n.original_invoice_id}<ExternalLink className="w-3 h-3" />
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(n.grand_total || n.amount)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${STATUS_BADGE[n.status] || "bg-slate-100 text-slate-600"}`}>{n.status || "draft"}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {n.issue_date ? formatIndianDate(n.issue_date) : (n.created_at ? formatIndianDate(n.created_at) : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
